import React from "react";
import { useClient } from "urql";
import IdentityCard from "../components/card";
import { SearchContext } from "../../common/context/search";
import { classNames } from "../../common/utils/classnames";
import {
  IdentitiesDocument,
  useIdentitiesQuery,
} from "../../graphql/generated";
import Loading from "../../common/components/loading";
import { Transition } from "@headlessui/react";
import Error from "../../common/components/error";
import { useTitle } from "../../common/utils/title";

const IdentityList: React.FC = () => {
  useTitle("Identities");
  const graphqlClient = useClient();
  const [loading, setLoading] = React.useState(false);
  const { searchBounced } = React.useContext(SearchContext);

  const [{ data, error, fetching }] = useIdentitiesQuery({
    variables: {
      first: 20,
      search: searchBounced,
    },
  });

  const fetchMore = React.useCallback(async () => {
    if (data?.identities.pageInfo.hasNextPage) {
      setLoading(true);
      await graphqlClient
        .query(IdentitiesDocument, {
          first: 20,
          after: data.identities.pageInfo.endCursor,
          search: searchBounced,
        })
        .toPromise();
      setLoading(false);
    }
  }, [data, graphqlClient, searchBounced]);

  return (
    <>
      <Loading show={!data && !error} />
      <Error error={error} />
      <Transition
        show={!!data}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4">
          <div className="sm:flex sm:items-center sm:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Identities
                <span className="text-sm text-gray-500 px-2">
                  {fetching ? "Loading..." : `${data?.identities.total}`}
                </span>
              </h2>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4">
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="p-4">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {data?.identities.edges?.map((identity) => (
                  <IdentityCard
                    identity={identity?.node}
                    key={identity?.node.id}
                  />
                ))}
              </div>
            </div>
          </div>
          {data &&
            data?.identities.total > (data?.identities.edges?.length ?? 0) && (
              <div className="min-w-full divide-y divide-gray-200">
                <div className="p-4">
                  <button
                    className={classNames(
                      loading
                        ? "bg-gray-500 text-white"
                        : "bg-zenlist-500 hover:bg-zenlist-700 text-white",
                      "font-bold py-2 px-4 rounded"
                    )}
                    onClick={() => fetchMore()}
                    disabled={loading}
                  >
                    Fetch More
                  </button>
                </div>
              </div>
            )}
        </div>
      </Transition>
    </>
  );
};

export default IdentityList;
